// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { CustomEvent } from '@shared/utils/CustomEvents';
import { Resources } from '@shared/localizations/Resources';
import { SessionSettingsConst } from './SessionSettingsConst';
import { AccountTradeStatus } from '@shared/utils/Account/AccountTradeStatus';
import { DirectReportMessage } from '@shared/utils/DirectMessages/DirectReportMessage';
import { TooltipType } from '@shared/utils/ReportMessageTooltipTypeEnum';
export class AccountFollowerService {
    static get IsShowAccountHidingWarning() {
        return (this.isShowAccountHidingWarning &&
            !Resources.isHidden(SessionSettingsConst.PANEL_COPY_TRADING_WARNING_HIDING_SETTINGS_TEXT));
    }
    static set IsShowAccountHidingWarning(value) {
        this.isShowAccountHidingWarning = value;
    }
    static get IsShowAfterReloginWarning() {
        return (this.isShowAfterReloginWarning &&
            !Resources.isHidden(SessionSettingsConst.PANEL_COPY_TRADING_WARNING_AFTER_RELOGIN_SETTINGS_TEXT));
    }
    static set IsShowAfterReloginWarning(value) {
        this.isShowAfterReloginWarning = value;
    }
    constructor(dataCache) {
        this.accountStatusMap = new Map();
        this.accountFollowersMap = new Map();
        this.onNeedRepopulate = new CustomEvent();
        this.onAccountUpdated = new CustomEvent();
        this.onAccountFollowerResponseReceived = new CustomEvent();
        this.dataCache = dataCache;
        dataCache.OnAddAccount.Subscribe(this.onAddAccountEvent.bind(this), this);
        dataCache.OnUpdateAccount.Subscribe(this.onUpdateAccountEvent.bind(this), this);
        dataCache.OnRemoveAccount.Subscribe(this.onRemoveAccountEvent.bind(this), this);
        this.initializeAccountStatusMap();
    }
    clear() {
        const DC = this.dataCache;
        DC.OnAddAccount.Subscribe(this.onAddAccountEvent.bind(this), this);
        DC.OnUpdateAccount.Subscribe(this.onUpdateAccountEvent.bind(this), this);
        DC.OnRemoveAccount.Subscribe(this.onRemoveAccountEvent.bind(this), this);
    }
    onDataLoadingFinished() {
        if (!AccountFollowerService.IsShowAfterReloginWarning) {
            return;
        }
        const masterAccountsWithFollowers = this.getSortedMasterAccounts().filter((acc) => acc.FollowerIDs.length > 0);
        if (masterAccountsWithFollowers.length === 0) {
            return;
        }
        const stringBuilder = [];
        masterAccountsWithFollowers.forEach((masterAccount) => {
            const masterAccountFormatted = Resources.getResource('panel.CopyTrading.Warning.MasterAccount', [
                masterAccount.toString()
            ]);
            const followersNames = masterAccount.FollowerIDs.map((id) => this.getAccountName(id.toString())).join(', ');
            stringBuilder.push(`${masterAccountFormatted} ${followersNames}`);
        });
        const caption = Resources.getResource('panel.CopyTrading.Warning.AfterRelogin.Caption');
        const messageTemplate = Resources.getResource('panel.CopyTrading.Warning.AfterRelogin.Message', [
            stringBuilder.join('\n')
        ]);
        this.showWarningMessage(caption, messageTemplate);
    }
    showWarningMessage(caption, message) {
        setTimeout(() => {
            const msg = new DirectReportMessage(caption, [['', message]]);
            msg.tooltipDrawType = TooltipType.FollowerInfo;
            this.dataCache.NewMessage(msg);
        }, 3000);
    }
    getSortedMasterAccounts() {
        return this.dataCache.getSortedAccountsArray();
    }
    getAccountName(accountId) {
        const account = this.dataCache.GetAccountById(accountId);
        return account ? account.toString() : accountId;
    }
    initializeAccountStatusMap() {
        const activeUserAccounts = this.dataCache.getSortedAccountsArray() || [];
        activeUserAccounts.forEach((account) => {
            this.accountStatusMap.set(account.AcctNumber, account.AccountTradeStatus);
            this.accountFollowersMap.set(account.AcctNumber, [...account.FollowerIDs]);
        });
    }
    onUpdateAccountEvent(account) {
        this.onAccountUpdated.Raise(account);
        const oldStatus = this.accountStatusMap.get(account.AcctNumber);
        const newStatus = account.AccountTradeStatus;
        if (oldStatus !== newStatus) {
            this.accountStatusMap.set(account.AcctNumber, newStatus);
            const ACTIVE = AccountTradeStatus.ACTIVE_STATUS;
            if (oldStatus === ACTIVE && newStatus !== ACTIVE) {
                this.showAccountHidingWarning(account);
            }
        }
        const oldFollowers = this.accountFollowersMap.get(account.AcctNumber) || [];
        const newFollowers = account.FollowerIDs;
        if (JSON.stringify(oldFollowers) !== JSON.stringify(newFollowers)) {
            this.accountFollowersMap.set(account.AcctNumber, [...newFollowers]);
        }
        this.onNeedRepopulate.Raise();
    }
    onRemoveAccountEvent(account) {
        this.accountStatusMap.delete(account.AcctNumber);
        this.accountFollowersMap.delete(account.AcctNumber);
        this.onNeedRepopulate.Raise();
    }
    onAddAccountEvent(account) {
        this.accountStatusMap.set(account.AcctNumber, account.AccountTradeStatus);
        this.accountFollowersMap.set(account.AcctNumber, [...account.FollowerIDs]);
        this.onNeedRepopulate.Raise();
    }
    showAccountHidingWarning(account) {
        if (!AccountFollowerService.isShowAccountHidingWarning)
            return;
        const caption = 'panel.CopyTrading.Warning.Caption';
        const message = this.getHidingWarningMessage(account);
        if (message) {
            const msg = new DirectReportMessage(caption, [['', message]]);
            msg.tooltipDrawType = TooltipType.FollowerInfo;
            this.dataCache.NewMessage(msg);
        }
    }
    getHidingWarningMessage(account) {
        const cachedFollowers = this.accountFollowersMap.get(account.AcctNumber) || [];
        if (JSON.stringify(cachedFollowers) !== JSON.stringify(account.FollowerIDs)) {
            return Resources.getResource('panel.CopyTrading.Warning.MasterHiding.Message', [account.toString()]);
        }
        const accounts = this.dataCache.getSortedAccountsArray() || [];
        if (accounts.some((acc) => acc.FollowerIDs.includes(account.ID))) {
            return Resources.getResource('panel.CopyTrading.Warning.FollowerHiding.Message', [account.toString()]);
        }
        return null;
    }
}
AccountFollowerService.isShowAccountHidingWarning = true;
AccountFollowerService.isShowAfterReloginWarning = true;
AccountFollowerService.showStartStopConfirmations = true;
